import { type ChangeEvent, useEffect, useState } from "react";

import {
  Minus as MinusIcon,
  Plus as PlusIcon,
  Trash2 as Trash2Icon,
} from "lucide-react";
import { useMutation, useQueryClient } from "tanstack-react-query-5";

import { Box } from "@/shared-web.ui/ui.box";
import { Checkbox } from "@/shared-web.ui/ui.checkbox";
import { Flex } from "@/shared-web.ui/ui.flex";
import { IconButton } from "@/shared-web.ui/ui.icon-button";
import { HardLink } from "@/shared-web.ui/ui.link";
import { cn } from "@/shared-web.ui/util.cn";
import { removeItems, setItemQty } from "@/stf.data/request.cart";
import {
  type CartItem as CartItemType,
} from "@/stf.data/schema";
import { PrimProductDetail } from "@/stf.ui/prim.product-detail";
import { PrimProductSmallDetail } from "@/stf.ui/prim.product-small-detail";

import { useCartContext } from "./cart.context";

interface CartItemProps extends CartItemType {}

const CartItem = (props: CartItemProps) => {
  const {
    title,
    cartItemId,
    selectedVariant,
    qty,
    selectedVariant: {
      retailPrice,
      promoPrice,
      stock,
    },
    thumbnailImg,
    shopDetail: {
      shopSlug,
    },
    productSlug,
    disabled,
    deliveryTime,
  } = props;

  const { dispatchCartAction, cartState } = useCartContext();
  const { selectedCartItemId } = cartState;
  const [itemQtyInput, setItemQtyInput] = useState<string>(qty.toString());

  const itemOutOfStock = stock === 0;
  const itemDisabled = disabled;
  const itemIsDisabled = itemOutOfStock || itemDisabled;

  const itemCheckedState = selectedCartItemId.includes(cartItemId);

  const queryClient = useQueryClient();

  const { mutate: mutateItemQty } = useMutation({
    mutationFn: setItemQty,
    onSettled: async () => {
      await queryClient.invalidateQueries(
        { queryKey: ["getCart"] },
      );
    },
  });

  const { mutate: mutateRemoveItem } = useMutation({
    mutationFn: removeItems,
    onSettled: async () => {
      await queryClient.invalidateQueries(
        { queryKey: ["getCart"] },
      );
    },
  });

  // handle qty changes, when adding same item
  useEffect(() => {
    setItemQtyInput(qty.toString());
  }, [qty]);

  /** Click handlering */
  const handleCheck = () => {
    dispatchCartAction({
      type: "toggleItem",
      payload: cartItemId,
    });
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setItemQtyInput(e.target.value);

    const q = parseInt(e.target.value, 10);

    if (typeof q === "number" && q > 0) {
      mutateItemQty({
        cartItemId,
        qty: q,
      });
    }
  };

  const handleInputBlur = (e: ChangeEvent<HTMLInputElement>) => {
    setItemQtyInput(qty.toString());
  };

  const handleAddItem = () => {
    const q = qty + 1;

    setItemQtyInput(q.toString());

    mutateItemQty({
      cartItemId,
      qty: q,
    });
  };

  const handleMinusItem = () => {
    const q = (qty === 1) ? qty : qty - 1;

    setItemQtyInput(q.toString());

    mutateItemQty({
      cartItemId,
      qty: q,
    });
  };

  const handleRemoveItem = () => {
    mutateRemoveItem({
      cartItemIds: [cartItemId],
    });
  };

  const productHref = `/shop/${shopSlug}/${productSlug}`;

  return (
    <Flex
      align="start"
      direction="row"
      gap={{
        initial: "2",
        xs: "3",
      }}
    >
      <Box mt="1">
        <Checkbox
          checked={itemCheckedState}
          onCheckedChange={handleCheck}
          disabled={itemIsDisabled}
          size="2"
        />
      </Box>
      <Flex
        direction="column"
        flexGrow="1"
        gap="2"
      >
        <HardLink href={productHref}>
          <PrimProductSmallDetail.Title>
            {title}
          </PrimProductSmallDetail.Title>
        </HardLink>
        <Flex
          className="w-full"
          direction="row"
          gap="3"
        >
          <HardLink href={productHref}>
            <PrimProductSmallDetail.Image
              alt={title}
              src={thumbnailImg}
            />
          </HardLink>
          <Flex
            direction="column"
            flexGrow="1"
            gap={{
              initial: "1",
              sm: "3",
              xs: "2",
            }}
            justify="between"
          >
            <PrimProductSmallDetail.SelectedVariant
              selectedVariant={selectedVariant}
            />
            <Flex>
              <PrimProductDetail.ShippingDetail
                deliveryTime={deliveryTime}
                disabled={disabled}
                stock={stock}
                size="1"
              />
            </Flex>
          </Flex>
          <Flex
            direction="column"
            justify="between"
          >
            <Flex
              direction="column"
            >
              {itemDisabled
                ? null
                : (
                    <PrimProductSmallDetail.Price
                      finalPrice={promoPrice}
                      stickerPrice={retailPrice}
                      finalPriceOrder="top"
                    />
                  )}
            </Flex>
            <Flex gap="2">
              {
                qty === 1 || itemDisabled
                  ? (
                      <IconButton
                        onClick={handleRemoveItem}
                        radius="large"
                        size="1"
                        variant="soft"
                        screenReaders={`Delete ${title}`}
                        highContrast={itemDisabled}
                      >
                        <Trash2Icon
                          className="size-4"
                          strokeWidth="1"
                        />
                      </IconButton>
                    )
                  : (
                      <IconButton
                        onClick={handleMinusItem}
                        radius="large"
                        size="1"
                        variant="soft"
                        screenReaders={`Decrease quantity of ${title} by 1`}
                      >
                        <MinusIcon className="size-4" />
                      </IconButton>
                    )
              }

              <input
                className={cn(
                  // removing number field appearance (up/down arrows)
                  // https://stackoverflow.com/questions/71296535/how-to-remove-arrow-on-input-type-number-with-tailwind-css
                  `
                    [&::-webkit-inner-spin-button]:appearance-none
                    [&::-webkit-outer-spin-button]:appearance-none
                    [appearance:textfield]
                  `,
                  `
                    w-6 text-center
                    disabled:bg-panel-solid
                    disabled:text-gray-8
                  `,
                )}
                type="number"
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                value={itemQtyInput}
                disabled={itemDisabled}
              />
              <IconButton
                onClick={handleAddItem}
                radius="large"
                size="1"
                variant="soft"
                disabled={itemDisabled}
                screenReaders={`Increase quantity of ${title} by 1`}
              >
                <PlusIcon
                  className="size-4"
                  strokeWidth="1"
                />
              </IconButton>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};
export {
  CartItem,
  type CartItemProps,
};
